import { useMemo, useState } from "react";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");

type ProbeResult = {
  label: string;
  url: string;
  ok: boolean;
  status?: number;
  statusText?: string;
  detail?: string;
  durationMs: number;
};

async function runProbe(label: string, url: string): Promise<ProbeResult> {
  const startedAt = Date.now();
  try {
    const res = await fetch(url, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
    });
    return {
      label,
      url,
      ok: res.ok,
      status: res.status,
      statusText: res.statusText,
      durationMs: Date.now() - startedAt,
    };
  } catch (e: any) {
    return {
      label,
      url,
      ok: false,
      detail: String(e?.message || e),
      durationMs: Date.now() - startedAt,
    };
  }
}

export default function NetworkDebug() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ProbeResult[]>([]);
  const [testedAt, setTestedAt] = useState<string>("");

  const probes = useMemo(
    () => [
      { label: "same-origin ping", url: `${window.location.origin}/favicon.ico` },
      { label: "relative /api", url: `${window.location.origin}/api/public/site-flags` },
      {
        label: "VITE_API_BASE /api",
        url: `${API_BASE || window.location.origin}/api/public/site-flags`,
      },
    ],
    []
  );

  const runAll = async () => {
    setLoading(true);
    try {
      const items = await Promise.all(probes.map((p) => runProbe(p.label, p.url)));
      setResults(items);
      setTestedAt(new Date().toLocaleString());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 text-slate-900">
      <h1 className="mb-3 text-xl font-bold">Network Debug</h1>
      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 text-sm">
        <p><b>location</b>: {window.location.href}</p>
        <p><b>origin</b>: {window.location.origin}</p>
        <p><b>VITE_API_BASE</b>: {API_BASE || "(empty)"}</p>
        <p><b>navigator.onLine</b>: {String(navigator.onLine)}</p>
        <p className="break-all"><b>userAgent</b>: {navigator.userAgent}</p>
      </div>

      <button
        type="button"
        onClick={runAll}
        disabled={loading}
        className="mb-4 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
      >
        {loading ? "Testing..." : "Run Connectivity Test"}
      </button>

      {testedAt ? <p className="mb-2 text-xs text-slate-600">testedAt: {testedAt}</p> : null}

      <div className="space-y-3">
        {results.map((r) => (
          <div key={r.label} className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
            <p><b>{r.label}</b></p>
            <p className="break-all"><b>url</b>: {r.url}</p>
            <p><b>ok</b>: {String(r.ok)}</p>
            <p><b>status</b>: {r.status ?? "-"}</p>
            <p><b>statusText</b>: {r.statusText ?? "-"}</p>
            <p className="break-all"><b>detail</b>: {r.detail ?? "-"}</p>
            <p><b>durationMs</b>: {r.durationMs}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
